import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Menu, ChevronRight, Building2, ShoppingCart } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { toggleSidebar } from '../../store/slices/uiSlice';
import { setActiveBranch } from '../../store/slices/authSlice';
import api from '../../services/api';
import { Sidebar } from './Sidebar';
import { FormSelect, SelectOption } from '../ui/FormSelect';
import type { StylesConfig } from 'react-select';
import { hasModuleAccess } from '../../utils/moduleAccess';

/* page-title map */
const TITLES: Record<string, string> = {
  '/dashboard':     'Dashboard',
  '/branches':      'Branch Management',
  '/services':      'Service Catalog',
  '/staff':         'Staff Management',
  '/clients':       'Customer Management',
  '/calendar':      'Appointments',
  '/invoices':      'Billing & Invoicing',
  '/inventory':     'Inventory & Products',
  '/expenses':      'Expenses',
  '/payroll':       'Payroll & Commission',
  '/marketing':     'Marketing & Campaigns',
  '/reports':       'Reports & Analytics',
  '/membership':    'Memberships',
  '/vouchers':      'Voucher Management',
  '/loyalty':       'Rewards Program',
  '/leads':         'Leads & CRM',
  '/settings':      'Settings',
};

/* ── styled ─────────────────────────────────────────────────────────────── */
const Root = styled.div`
  display: flex;
  min-height: 100vh;
  background: #F8F8F8;
  /* On mobile, content occupies full width (sidebar overlays on top) */
  /* On desktop, sidebar is in-flow and pushes content */
`;

const Main = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  /* On mobile add left padding so content isn't under a fixed sidebar */
  @media (max-width: 1023px) { width: 100%; }
`;

const TopBar = styled.header`
  position: sticky; top: 0; z-index: 100;
  background: #fff;
  border-bottom: 1px solid #EEEEEE;
  height: 58px;
  display: flex; align-items: center;
  padding: 0 20px;
  gap: 12px;
`;

const HamburgerBtn = styled.button`
  background: none; border: none; cursor: pointer;
  width: 34px; height: 34px; border-radius: 7px;
  display: flex; align-items: center; justify-content: center;
  color: #717171; transition: background 0.14s;
  svg { width: 18px; height: 18px; }
  &:hover { background: #F5F5F5; color: #1A1A1A; }
  @media (min-width: 1024px) { display: none; }
`;

const Breadcrumb = styled.div`
  display: flex; align-items: center; gap: 6px;
  flex: 1; min-width: 0;
  font-size: 13px; color: #ADADAD; font-weight: 500;
  span.active { color: #1A1A1A; font-weight: 700; font-size: 15px; }
  svg { width: 14px; height: 14px; flex-shrink: 0; }
`;

const BranchSelectWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  height: 36px;
  min-width: 178px;
  max-width: 230px;
  border: 1px solid #E5E1DD;
  border-radius: 8px;
  background: #FCFAF8;
  color: #795E49;
  transition: border-color 0.14s, background 0.14s;

  &:focus-within {
    border-color: #BC9979;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(188, 153, 121, 0.14);
  }

  > svg:first-child {
    width: 15px;
    height: 15px;
    margin-left: 10px;
    flex-shrink: 0;
  }

  @media (max-width: 900px) { min-width: 150px; max-width: 170px; }
  @media (max-width: 640px) { min-width: 42px; width: 42px; }
`;

const BranchSelectSlot = styled.div`
  min-width: 0;
  width: 100%;
  height: 100%;

  @media (max-width: 640px) {
    position: absolute;
    inset: 0;
    opacity: 0;
    padding: 0;
  }
`;

const TopActions = styled.div`
  display: flex; align-items: center; gap: 8px; margin-left: auto;
`;

const POSShortcut = styled.button`
  height: 34px; border: 1px solid #E4DDD6; background: #F8F3EE; color: #835B43;
  border-radius: 8px; padding: 0 11px; cursor: pointer;
  display: flex; align-items: center; gap: 7px;
  font-size: 12px; font-weight: 750; transition: background .16s, border-color .16s, transform .16s;
  svg { width: 16px; height: 16px; }
  &:hover { background: #F1E6DC; border-color: #CDB29A; }
  &:active { transform: scale(.98); }
  @media (max-width: 640px) { span { display: none; } padding: 0 9px; }
`;

const UserChip = styled.div`
  display: flex; align-items: center; gap: 8px;
  padding: 4px 10px 4px 4px; border-radius: 20px;
  cursor: pointer; transition: background 0.14s;
  &:hover { background: #F5F5F5; }
`;
const TopAvatar = styled.div`
  width: 28px; height: 28px; border-radius: 50%;
  background: #BC9979;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700; color: #fff;
`;
const TopUserText = styled.div`
  p { font-size: 13px; font-weight: 600; color: #1A1A1A; margin: 0; line-height: 1.2; }
  span { font-size: 11px; color: #ADADAD; }
`;

const Content = styled.div`
  flex: 1; padding: 24px;
  @media (max-width: 640px) { padding: 14px; }
`;

const Overlay = styled.div<{ $v: boolean }>`
  display: ${p => p.$v ? 'block' : 'none'};
  position: fixed; inset: 0; background: rgba(0,0,0,0.35);
  z-index: 199;
  @media (min-width: 1024px) { display: none; }
`;

interface AvailableBranch {
  _id: string;
  name: string;
  code: string;
}

const branchSelectStyles: StylesConfig<SelectOption, boolean> = {
  control: base => ({ ...base, minHeight: 34, height: 34, border: 0, boxShadow: 'none', background: 'transparent', cursor: 'pointer' }),
  valueContainer: base => ({ ...base, padding: '0 6px 0 8px' }),
  singleValue: base => ({ ...base, color: '#4D4036', fontSize: 12, fontWeight: 700 }),
  placeholder: base => ({ ...base, fontSize: 12, color: '#A09A93' }),
  indicatorsContainer: base => ({ ...base, height: 34 }),
  dropdownIndicator: (base, state) => ({ ...base, color: '#795E49', padding: '5px 8px 5px 4px', transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : undefined }),
  indicatorSeparator: () => ({ display: 'none' }),
  menu: base => ({ ...base, minWidth: 220, right: 0, left: 'auto', zIndex: 9999 }),
  menuPortal: base => ({ ...base, zIndex: 10000 }),
  option: (base, state) => ({ ...base, fontSize: 12, color: '#4D4036', background: state.isSelected ? '#F4E8DC' : state.isFocused ? '#FAF5F0' : '#fff', cursor: 'pointer' }),
};

/* ── component ─────────────────────────────────────────────────────────── */
export const MainLayout: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { sidebarOpen } = useAppSelector(s => s.ui);
  const { user, activeBranch, roleAccess } = useAppSelector(s => s.auth);
  const location        = useLocation();
  const [branches, setBranches] = useState<AvailableBranch[]>([]);
  const [branchesLoading, setBranchesLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get('/branches/available')
      .then(response => {
        if (mounted) setBranches(response.data.data ?? []);
      })
      .catch(() => {
        if (mounted) setBranches([]);
      })
      .finally(() => {
        if (mounted) setBranchesLoading(false);
      });

    return () => { mounted = false; };
  }, []);

  const segment   = '/' + location.pathname.split('/')[1];
  const pageTitle = TITLES[segment] ?? 'ZENZI';
  const initials  = user ? `${user.firstName?.[0]??''}${user.lastName?.[0]??''}`.toUpperCase() : 'A';
  const canSwitchBranches = user ? ['admin', 'super_admin'].includes(user.role) : false;
  const selectedBranch = canSwitchBranches
    ? activeBranch ?? localStorage.getItem('branchId') ?? user?.defaultBranch ?? ''
    : user?.defaultBranch ?? '';

  useEffect(() => {
    if (!user || canSwitchBranches || !user.defaultBranch) return;
    if (activeBranch !== user.defaultBranch) dispatch(setActiveBranch(user.defaultBranch));
  }, [activeBranch, canSwitchBranches, dispatch, user]);

  const handleBranchChange = (option: SelectOption | SelectOption[] | null) => {
    if (!canSwitchBranches) return;
    const branchId = !Array.isArray(option) && option ? String(option.value) : '';
    if (!branchId || branchId === selectedBranch) return;
    dispatch(setActiveBranch(branchId));
    window.location.reload();
  };

  return (
    <Root>
      <Sidebar />
      <Overlay $v={sidebarOpen} onClick={() => dispatch(toggleSidebar())} />

      <Main>
        <TopBar>
          <HamburgerBtn onClick={() => dispatch(toggleSidebar())}>
            <Menu />
          </HamburgerBtn>

          <Breadcrumb>
            <span>Salon</span>
            <ChevronRight />
            <span className="active">{pageTitle}</span>
          </Breadcrumb>

          <BranchSelectWrap title={canSwitchBranches ? 'Select active branch' : 'Your default branch'}>
            <Building2 aria-hidden="true" />
            <BranchSelectSlot>
              <FormSelect
                aria-label="Active branch"
                options={branches.map(branch => ({ value: branch._id, label: branch.name }))}
                value={branches.filter(branch => branch._id === selectedBranch).map(branch => ({ value: branch._id, label: branch.name }))[0] ?? null}
                onChange={handleBranchChange}
                placeholder={branchesLoading ? 'Loading branches...' : 'No branches available'}
                isDisabled={!canSwitchBranches || branchesLoading || branches.length === 0}
                isClearable={false}
                isSearchable={branches.length > 6}
                sortAlpha={false}
                styles={branchSelectStyles}
              />
            </BranchSelectSlot>
          </BranchSelectWrap>

          <TopActions>
            {hasModuleAccess(roleAccess,'pos',user?.permissions)&&<POSShortcut onClick={() => navigate('/pos')} aria-label="Open Front Desk POS" title="Open Front Desk POS">
              <ShoppingCart />
              <span>POS</span>
            </POSShortcut>}
            <UserChip>
              <TopAvatar>{initials}</TopAvatar>
              <TopUserText>
                <p>{user?.firstName ?? 'User'} {user?.lastName ?? ''}</p>
                <span>{user?.role?.replace(/_/g,' ') ?? 'Staff'}</span>
              </TopUserText>
            </UserChip>
          </TopActions>
        </TopBar>

        <Content>
          <Outlet />
        </Content>
      </Main>
    </Root>
  );
};

export default MainLayout;
